import { useTheme } from "@emotion/react";
import { useCallback, useEffect, useState } from "react";

const gridSize = 20;
const initialSnake = [{ x: 10, y: 10 }];
const initialDirection = { x: 0, y: -1 };
const initialFood = { x: 5, y: 5 };
const gameSpeed = 200; // Time interval in ms for the game loop

type SnakeSegment = { x: number; y: number };

const SnakeGame = () => {
    const theme = useTheme();
    const [snake, setSnake] = useState(initialSnake);
    const [direction, setDirection] = useState(initialDirection);
    const [food, setFood] = useState(initialFood);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);

    // Handle keyboard input for direction change
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        const { key } = event;

        setDirection(direction => {
            if (key === "ArrowUp" && direction.y === 0) return { x: 0, y: -1 };
            if (key === "ArrowDown" && direction.y === 0) return { x: 0, y: 1 };
            if (key === "ArrowLeft" && direction.x === 0)
                return { x: -1, y: 0 };
            if (key === "ArrowRight" && direction.x === 0)
                return { x: 1, y: 0 };
            return direction;
        });
    }, []);

    // Check if the snake collides with itself or the walls
    const checkCollision = useCallback(
        (head: SnakeSegment) => {
            // Check if the snake hits the wall
            if (
                head.x < 0 ||
                head.x >= gridSize ||
                head.y < 0 ||
                head.y >= gridSize
            ) {
                return true;
            }
            // Check if the snake hits itself
            return snake.some(
                segment => segment.x === head.x && segment.y === head.y,
            );
        },
        [snake],
    );

    // Generate random coordinates for the food
    const generateFood = useCallback(() => {
        let newFood: SnakeSegment;
        do {
            newFood = {
                x: Math.floor(Math.random() * gridSize),
                y: Math.floor(Math.random() * gridSize),
            };
        } while (
            snake.some(
                segment => segment.x === newFood.x && segment.y === newFood.y,
            )
        );
        return newFood;
    }, [snake]);

    // Main game loop
    useEffect(() => {
        if (gameOver) return; // Stop the game if it's over

        const timer = setInterval(() => {
            const newSnake = [...snake];
            const head = {
                x: newSnake[0].x + direction.x,
                y: newSnake[0].y + direction.y,
            };

            // Check for collisions
            if (checkCollision(head)) {
                setGameOver(true);
                return;
            }

            // Add new head to the snake
            newSnake.unshift(head);

            // Check if the snake eats food
            if (head.x === food.x && head.y === food.y) {
                setScore(score + 1);
                setFood(generateFood()); // Generate new food
            } else {
                newSnake.pop(); // Remove the last segment if no food is eaten
            }

            setSnake(newSnake);
        }, gameSpeed);

        return () => clearInterval(timer);
    }, [snake, direction, food, gameOver, score, checkCollision, generateFood]);

    // Handle keyboard input
    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);

    // Restart the game
    const restartGame = () => {
        setSnake(initialSnake);
        setDirection(initialDirection);
        setFood(initialFood);
        setGameOver(false);
        setScore(0);
    };

    return (
        <div>
            <h1>Snake Game</h1>
            <h2>Score: {score}</h2>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${gridSize}, 20px)`,
                    gridTemplateRows: `repeat(${gridSize}, 20px)`,
                    gap: "1px",
                    justifyContent: "center",
                }}>
                {/* Generate the grid */}
                {[...Array(gridSize * gridSize)].map((_, index) => {
                    const x = index % gridSize;
                    const y = Math.floor(index / gridSize);
                    const isSnake = snake.some(
                        segment => segment.x === x && segment.y === y,
                    );
                    const isFood = food.x === x && food.y === y;

                    return (
                        <div
                            key={index}
                            style={{
                                width: "20px",
                                height: "20px",
                                backgroundColor: isSnake
                                    ? theme.colors.primary.asString
                                    : isFood
                                      ? theme.colors.purple.asString
                                      : "lightgray",
                                border: "1px solid #ddd",
                            }}
                        />
                    );
                })}
            </div>
            {gameOver && (
                <div>
                    <h2>Game Over</h2>
                    <button onClick={restartGame}>Restart Game</button>
                </div>
            )}
        </div>
    );
};

export { SnakeGame };
